'use client';

import React, { useEffect, useState } from 'react';
import type { ContentType as TContentType } from '@jlg-website/database/types';
import { ContentType } from '@jlg-website/shared/constants';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Pencil, Loader2 } from 'lucide-react';
import { formComponents } from './form-components';

export function ContentEditDialogForm({
  onEditAction,
  contentTypeName,
  contentId,
}: {
  onEditAction: () => void;
  contentTypeName: (typeof ContentType)[keyof typeof ContentType];
  contentId: number;
}) {
  const [open, setOpen] = useState(false);
  const [contentType, setContentType] = useState<TContentType | null>(null);
  const [defaultValues, setDefaultValues] = useState<Record<string, unknown> | null>(null);

  const handleSave = () => {
    setOpen(false);
    onEditAction();
  };

  useEffect(() => {
    if (!open) return;

    setContentType(null);
    setDefaultValues(null);

    const fetchData = async () => {
      const [ct, content] = await Promise.all([
        fetch(`/admin/api/content-type?name=${contentTypeName}`, { cache: 'no-store' }).then((r) =>
          r.json()
        ),
        fetch(`/admin/api/contents/${contentId}`, { cache: 'no-store' }).then((r) => r.json()),
      ]);
      setContentType(ct);
      setDefaultValues(content);
    };

    fetchData();
  }, [open]);

  const FormComponent = formComponents[contentTypeName] as React.ComponentType<{
    contentType: TContentType;
    defaultValues: Record<string, unknown>;
    onSave: () => void;
    customCancelButton: React.ReactNode;
  }> | null;

  return (
    <Dialog
      open={open}
      onOpenChange={(value) => {
        setOpen(value);
        if (!value) {
          setContentType(null);
          setDefaultValues(null);
        }
      }}
    >
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="w-full">
          <Pencil /> Edit selected
        </Button>
      </DialogTrigger>
      <DialogContent className="lg:min-w-3xl gap-0 p-0">
        <DialogHeader className="shrink-0 px-6 pt-6 pb-0">
          <DialogTitle>Edit content</DialogTitle>
        </DialogHeader>
        {!contentType || !defaultValues ? (
          <div className="flex justify-center items-center py-12">
            <Loader2 className="size-8 animate-spin text-muted-foreground" />
          </div>
        ) : (
          FormComponent && (
            <div className="flex-1 overflow-y-auto px-6 pt-4 pb-6">
              <FormComponent
                contentType={contentType}
                defaultValues={defaultValues}
                onSave={handleSave}
                customCancelButton={
                  <DialogClose asChild>
                    <Button variant="outline">Cancel</Button>
                  </DialogClose>
                }
              />
            </div>
          )
        )}
      </DialogContent>
    </Dialog>
  );
}
